import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isValid, parseISO } from 'date-fns';
import { didCancel, dropTask, restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { modifier } from 'ember-modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not } from 'ember-truth-helpers';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import UiCard from 'garaje/components/ui/card';
import type { DocumentStatus } from 'garaje/components/visitors/document-status-badge';
import DocumentStatusBadge from 'garaje/components/visitors/document-status-badge';
import DocumentPreviewModal from 'garaje/components/visitors/profiles/document-preview-modal';
import type { ProfileDocument } from 'garaje/components/visitors/profiles/document-status';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';

interface Signature {
  Args: {
    document?: VisitorDocumentModel;
    template?: CustomUserDocumentTemplateModel;
    configuration?: UserDocumentTemplateConfigurationModel;
    isUploaded: boolean;
    showUndo?: boolean;
    onStatusChange?: () => void;
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewCard extends Component<Signature> {
  @service declare store: StoreService;
  @tracked expirationEntries: Array<{ name: string; date: Date | null }> = [];
  /** Tracks which calendar popover is open: a numeric index, or null */
  @tracked openCalendar: number | null = null;
  private expirationFieldsElement: HTMLElement | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.initExpirationEntries();
  }

  private parseDate(dateStr: string): Date | null {
    // Extract date portion to avoid UTC off-by-one for ISO timestamps
    // e.g. "2026-10-15T00:00:00.000Z" should parse as Oct 15, not Oct 14 in PST
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (dateMatch) {
      const d = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : null;
  }

  private initExpirationEntries(): void {
    const data = this.args.document?.inputFieldsData ?? {};

    const parsed = Object.entries(data)
      .filter(([, v]) => typeof v === 'string')
      .map(([name, dateStr]) => ({ name, date: this.parseDate(String(dateStr)) }))
      .filter((e): e is { name: string; date: Date } => e.date !== null);

    this.expirationEntries = parsed.length === 0 && this.requiresExpirationDate ? [{ name: '', date: null }] : parsed;
  }

  get title(): string | undefined {
    return this.args.isUploaded ? this.args.document?.title : this.args.template?.title;
  }

  get status(): DocumentStatus {
    if (!this.args.isUploaded) return 'not-uploaded';
    const doc = this.args.document;
    if (doc?.isApproved) return 'approved';
    if (doc?.isDenied) return 'denied';
    return 'review';
  }

  get uploadDate(): string | null {
    const attachment = this.args.document?.mostRecentAttachment;
    const date = attachment?.updatedAt;
    if (!date) {
      const docDate = this.args.document?.updatedAt;
      return docDate ? format(docDate, 'MMM d, yyyy, h:mm a') : null;
    }
    return format(date, 'MMM d, yyyy, h:mm a');
  }

  get externalAttachmentUrl(): string | null {
    return this.args.document?.externalAttachments?.[0]?.url ?? null;
  }

  @tracked imageFailedToLoad = false;

  get isPdf(): boolean {
    return this.imageFailedToLoad;
  }

  // The document is "discarded" once the backend has anonymized the
  // attachment, signalled by an `anonymized_at` timestamp on the first
  // external attachment. When set, the preview area renders the "Document
  // discarded" placeholder instead of the regular preview/PDF.
  get isDiscarded(): boolean {
    // Top-level anonymizedAt is set for both Veriff and profile docs. Fall back to
    // the per-attachment flag; redacted Veriff docs have an empty externalAttachments
    // array, so the per-attachment check alone would miss them.
    return !!(this.args.document?.anonymizedAt ?? this.args.document?.externalAttachments?.[0]?.anonymized_at);
  }

  get mediaOverlayClass(): string {
    return 'absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 group-hover:bg-black/40 group-hover:opacity-100 transition-all duration-200 pointer-events-none';
  }

  @action handleImageError(): void {
    this.imageFailedToLoad = true;
  }

  get pdfViewerUrl(): string {
    return `${this.externalAttachmentUrl}#navpanes=0&toolbar=0`;
  }

  get documentFileName(): string {
    return this.title ?? 'document.pdf';
  }

  @tracked showPreviewModal = false;

  get previewDocuments(): ProfileDocument[] {
    return [this.previewDocument];
  }

  get previewDocument(): ProfileDocument {
    return {
      id: this.args.document?.id ?? '',
      title: this.title ?? '',
      downloadUrl: this.externalAttachmentUrl ?? undefined,
      approvalStatus: this.args.document?.isApproved ? 'approved' : this.args.document?.isDenied ? 'denied' : 'review',
      createdAt: this.args.document?.createdAt?.toISOString(),
      fieldValues: this.args.document?.inputFieldsData,
    };
  }

  @action openDocument(): void {
    this.showPreviewModal = true;
  }

  @action closePreviewModal(): void {
    this.showPreviewModal = false;
  }

  get documentLink(): VisitorDocumentModel['userDocumentLink'] {
    return this.args.document?.userDocumentLink;
  }

  get isPending(): boolean {
    return this.status === 'review';
  }

  get requiresExpirationDate(): boolean {
    return this.args.configuration?.reUploadConfig?.policy === 'on_expiration';
  }

  get hasAnyCompleteDate(): boolean {
    return this.expirationEntries.some((e) => e.name && e.date);
  }

  get approveDisabled(): boolean {
    if (this.approveTask.isRunning) return true;
    if (this.requiresExpirationDate && !this.hasAnyCompleteDate) return true;
    if (this.hasIncompleteExpirationEntries) return true;
    return false;
  }

  formatDate = (date: Date | null): string => (date ? format(date, 'MMM d, yyyy') : 'Select date');

  @action toggleCalendar(id: number): void {
    if (this.openCalendar === id) {
      this.closeCalendar();
    } else {
      this.openCalendar = id;
      requestAnimationFrame(() => {
        if (this.isDestroying || this.isDestroyed) return;
        document.addEventListener('click', this.handleOutsideClick, true);
      });
    }
  }

  captureExpirationFields = modifier((element: Element) => {
    this.expirationFieldsElement = element as HTMLElement;
  });

  handleOutsideClick = (event: MouseEvent): void => {
    if (this.expirationFieldsElement?.contains(event.target as Node)) return;
    this.closeCalendar();
  };

  closeCalendar = (): void => {
    this.openCalendar = null;
    document.removeEventListener('click', this.handleOutsideClick, true);
  };

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('click', this.handleOutsideClick, true);
  }

  get isDecided(): boolean {
    return this.status === 'approved' || this.status === 'denied';
  }

  @action addExpirationRow(): void {
    this.expirationEntries = [...this.expirationEntries, { name: '', date: null }];
  }

  get canRemoveRow(): boolean {
    return !this.requiresExpirationDate || this.expirationEntries.length > 1;
  }

  @action removeExpirationRow(index: number): void {
    if (!this.canRemoveRow) return;
    this.expirationEntries = this.expirationEntries.filter((_, i) => i !== index);
    void this.saveExpirationDatesTask.perform();
  }

  @action updateExpirationName(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.expirationEntries = this.expirationEntries.map((e, i) => (i === index ? { ...e, name: value } : e));
    void this.saveExpirationDatesTask.perform();
  }

  @action selectExpirationDate(index: number, date: Date): void {
    this.expirationEntries = this.expirationEntries.map((e, i) => (i === index ? { ...e, date } : e));
    this.closeCalendar();
    void this.saveExpirationDatesTask.perform();
  }

  private buildInputFieldsData(): Record<string, string> {
    const data: Record<string, string> = {};
    for (const entry of this.expirationEntries) {
      if (entry.name && entry.date) {
        data[entry.name] = format(entry.date, 'yyyy-MM-dd');
      }
    }
    return data;
  }

  get hasIncompleteExpirationEntries(): boolean {
    return this.expirationEntries.some((e) => {
      const hasName = Boolean(e.name);
      const hasDate = e.date !== null;
      return hasName !== hasDate;
    });
  }

  // Send a targeted PATCH with input_fields_data. Include `active` to
  // preserve its current value — the response pushes all attributes
  // back into the store.
  private async patchInputFieldsData(): Promise<void> {
    const doc = this.args.document;
    if (!doc) return;
    const data = this.buildInputFieldsData();
    const adapter = this.store.adapterFor('visitor-document');
    const url = adapter.buildURL('visitor-document', doc.id, null, 'v3');
    await adapter.ajax(url, 'PATCH', {
      data: {
        data: {
          type: 'visitor-documents',
          id: doc.id,
          attributes: {
            'input-fields-data': data,
            active: doc.active,
          },
        },
      },
    });
    doc.inputFieldsData = Object.freeze({ ...data });
  }

  // Debounced auto-save: restartable so rapid edits collapse into one PATCH.
  // Skips save if any entry is incomplete (name without date or vice versa).
  saveExpirationDatesTask = restartableTask(async () => {
    try {
      await timeout(500);
      if (this.hasIncompleteExpirationEntries) return;
      await this.patchInputFieldsData();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  approveTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.patchInputFieldsData();
      await this.documentLink?.approve();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  denyTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.documentLink?.deny();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  resetTask = dropTask(async () => {
    try {
      await this.documentLink?.reset();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  <template>
    <UiCard @border={{true}} class="rounded-lg p-6 mb-4 overflow-visible" data-test-document-review-card ...attributes>
      {{! Header: title + status badge }}
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold m-0" data-test-document-title>{{this.title}}</h3>
        <DocumentStatusBadge @status={{this.status}} />
      </div>

      {{#if @isUploaded}}
        {{! Upload date }}
        {{#if this.uploadDate}}
          <p class="text-sm text-carbon-50 mt-0 mb-4" data-test-upload-date>
            Uploaded
            {{this.uploadDate}}
          </p>
        {{/if}}

        {{! Document preview }}
        {{#if this.isDiscarded}}
          <div class="rounded-md border border-carbon-10 overflow-hidden mb-4 bg-carbon-5" data-test-document-discarded>
            <div class="w-full min-h-80 flex items-center justify-center">
              <span class="text-carbon-70 text-sm font-normal italic">Document discarded</span>
            </div>
          </div>
        {{else if this.externalAttachmentUrl}}
          <div
            class="group relative rounded-md border border-carbon-20 overflow-hidden mb-4 bg-carbon-10"
            data-test-document-preview
          >
            {{#if this.isPdf}}
              <iframe
                src={{this.pdfViewerUrl}}
                title={{this.documentFileName}}
                class="w-full min-h-80 border-0"
                data-private
              />
              {{! Transparent div captures hover over iframe so group-hover triggers }}
              <div class="absolute inset-0"></div>
            {{else}}
              <img
                src={{this.externalAttachmentUrl}}
                alt={{this.title}}
                class="w-full object-contain max-h-80"
                data-private
                {{on "error" this.handleImageError}}
              />
            {{/if}}
            <div class={{this.mediaOverlayClass}}>
              <a
                href={{this.externalAttachmentUrl}}
                download
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn--small btn--outline flex items-center gap-2 !bg-carbon-5 !rounded-md pointer-events-auto"
                data-test-download-button
              >
                {{svgJar "download" width="14" height="14" class="shrink-0 -mt-1"}}
                <span>Download</span>
              </a>
              <button
                type="button"
                class="btn btn--small btn--outline flex items-center gap-2 !bg-carbon-5 !rounded-md pointer-events-auto"
                {{on "click" this.openDocument}}
                data-test-open-button
              >
                {{svgJar "expand-corners" width="12" height="12" class="shrink-0 -mt-1"}}
                <span>Open</span>
              </button>
            </div>
          </div>

          {{#if this.showPreviewModal}}
            <DocumentPreviewModal
              @documents={{this.previewDocuments}}
              @initialIndex={{0}}
              @onClose={{this.closePreviewModal}}
            />
          {{/if}}
        {{/if}}

        {{! Expiration date fields }}
        <div class="mt-4" data-test-expiration-fields {{this.captureExpirationFields}}>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-carbon-50 leading-none">Expiration date(s)</span>
            <button
              class="text-sm text-gem-50 hover:underline"
              type="button"
              data-test-add-expiration
              {{on "click" this.addExpirationRow}}
            >
              Add
            </button>
          </div>

          {{! Expiration entries }}
          {{#each this.expirationEntries as |entry index|}}
            <div class="flex items-center gap-2 mb-3" data-test-expiration-row>
              <input
                type="text"
                value={{entry.name}}
                placeholder="Name"
                class="flex-1 text-sm border rounded px-3 h-10 !mb-0
                  {{if (and (not entry.name) entry.date) 'border-red-50' 'border-carbon-20'}}"
                required
                data-test-expiration-name
                {{on "change" (fn this.updateExpirationName index)}}
              />
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center justify-start gap-1.5 w-[130px] px-3 text-sm border rounded bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40 h-10
                    {{if (eq this.openCalendar index) 'border-gem-50' 'border-carbon-20'}}"
                  data-test-expiration-date
                  {{on "click" (fn this.toggleCalendar index)}}
                >
                  <span class="flex-none text-carbon-40">{{svgJar "calendar-16" width="16" height="16"}}</span>
                  <span class="{{unless entry.date 'text-carbon-40'}}">{{this.formatDate entry.date}}</span>
                </button>
                {{! template-lint-disable use-ui-card }}
                {{#if (eq this.openCalendar index)}}
                  <div
                    class="absolute right-0 z-50 mt-1 p-3 bg-white border border-carbon-20 rounded-lg shadow-lg"
                    data-test-expiration-calendar
                  >
                    <EnvoyOneUpCalendar
                      @selectedDate={{entry.date}}
                      @onSelectDate={{fn this.selectExpirationDate index}}
                    />
                  </div>
                {{/if}}
              </div>
              {{#if this.canRemoveRow}}
                <button
                  class="flex-none w-8 h-10 flex items-center justify-center text-carbon-50 hover:text-red-50"
                  type="button"
                  data-test-remove-expiration
                  {{on "click" (fn this.removeExpirationRow index)}}
                >
                  {{svgJar "cross-black" width="16" height="16" class="[&_path]:fill-current"}}
                </button>
              {{else}}
                <div class="flex-none w-8 h-10"></div>
              {{/if}}
            </div>
          {{/each}}
          {{#if this.hasIncompleteExpirationEntries}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-validation>
              Both name and date are required for each entry.
            </p>
          {{/if}}
        </div>

        {{! Approve / Deny / Undo buttons }}
        {{#if this.isPending}}
          {{#if (and this.requiresExpirationDate (not this.hasAnyCompleteDate))}}
            <p class="text-xs text-red-50 mt-2 mb-0" data-test-expiration-required>
              At least one expiration date is required before approving
            </p>
          {{/if}}
          <div class="flex gap-4 mt-4" data-test-document-actions>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-base font-medium text-carbon-70 hover:bg-cilantro-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-approve-button
              disabled={{this.approveDisabled}}
              type="button"
              {{on "click" (perform this.approveTask)}}
            >
              {{svgJar "checkmark-green" width="16" height="16"}}
              Approve
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-base font-medium text-carbon-70 hover:bg-red-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-deny-button
              disabled={{this.denyTask.isRunning}}
              type="button"
              {{on "click" (perform this.denyTask)}}
            >
              {{svgJar "cross-small-red" width="16" height="16"}}
              Deny
            </button>
          </div>
        {{else if (and this.isDecided @showUndo)}}
          <div class="flex mt-4" data-test-document-actions>
            <button
              class="px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-undo-button
              disabled={{this.resetTask.isRunning}}
              type="button"
              {{on "click" (perform this.resetTask)}}
            >
              Undo
            </button>
          </div>
        {{/if}}
      {{else}}
        {{! Not uploaded placeholder }}
        <div class="py-8 text-center text-carbon-50" data-test-not-uploaded-placeholder>
          Document not yet uploaded
        </div>
      {{/if}}
    </UiCard>
  </template>
}
